import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Calendar, Film, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaFilm } from '@/types';

export default function FilmsList() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: films, isLoading } = useQuery<CinemaFilm[]>({
    queryKey: ['cinema-films', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/films`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (filmId: string) => {
      await api.delete(`/admin/venues/${venueId}/cinema/films/${filmId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-films', venueId] });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'coming_soon':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Coming Soon
          </span>
        );
      case 'now_showing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Now Showing
          </span>
        );
      case 'ended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Ended
          </span>
        );
      default:
        return null;
    }
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Films</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - Manage films and schedule showings
          </p>
        </div>
        <Link to={`/venues/${venueId}/cinema/films/new`} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Film
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="card overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-200" />
              <div className="p-4">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))
        ) : films?.length === 0 ? (
          <div className="col-span-full card p-12 text-center text-gray-500">
            No films added yet. Add films to schedule showings.
          </div>
        ) : (
          films?.map((film) => (
            <div key={film.id} className="card overflow-hidden">
              <div className="h-48 bg-gray-200 relative">
                {film.poster_url ? (
                  <img
                    src={film.poster_url}
                    alt={film.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <Film className="h-16 w-16 text-gray-300" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  {getStatusBadge(film.status)}
                </div>
              </div>

              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{film.title}</h3>

                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  {film.duration_minutes && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDuration(film.duration_minutes)}
                    </span>
                  )}
                  {film.rating && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">
                      {film.rating}
                    </span>
                  )}
                  {film.genre && (
                    <span className="text-xs">{film.genre}</span>
                  )}
                </div>

                {film.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{film.description}</p>
                )}

                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Calendar className="h-3 w-3 mr-1" />
                    {film.showing_count || 0} showings
                  </span>

                  <div className="flex items-center gap-1">
                    <Link
                      to={`/venues/${venueId}/cinema/films/${film.id}/showings`}
                      className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                      title="Manage Showings"
                    >
                      <Calendar className="h-4 w-4" />
                    </Link>
                    <Link
                      to={`/venues/${venueId}/cinema/films/${film.id}`}
                      className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                      title="Edit Film"
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${film.title}" and all its showings?`)) {
                          deleteMutation.mutate(film.id);
                        }
                      }}
                      className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                      title="Delete Film"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
